import { Injectable, signal, inject } from '@angular/core';
import { supabase } from '../supabase';
import { Patient, ACS, Appointment } from '../models';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { CacheService } from './cache.service';
import { SecurityUtil } from '../utils/security.util';

@Injectable({
    providedIn: 'root'
})
export class PatientService {
    private loadingService = inject(LoadingService);
    private toast = inject(ToastService);
    private cache = inject(CacheService);

    public patientsTable = signal<Patient[]>([]);
    public totalPatients = signal<number>(0);

    private dbService: any = null;

    setDatabaseService(dbService: any) {
        this.dbService = dbService;
    }

    private ensureDbService(): any {
        if (!this.dbService) {
            console.warn('[PatientService] DatabaseService not set.');
        }
        return this.dbService;
    }

    static normalizeDate(val: any): string | null {
        if (!val) return null;
        if (val instanceof Date) {
            return isNaN(val.getTime()) ? null : val.toISOString().split('T')[0];
        }
        const s = String(val).trim();
        if (!s) return null;
        const brMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (brMatch) return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;
        const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) return isoMatch[1];
        return null;
    }

    async fetchPatientsPaginated(municipalityId: string | null, limit: number, offset: number, searchTerm: string = '') {
        const cacheKey = `patients_${municipalityId}_${limit}_${offset}_${searchTerm}`;
        
        if (!searchTerm && this.cache.has(cacheKey)) {
            const cached = this.cache.get<{ data: Patient[]; total: number }>(cacheKey);
            if (cached) {
                this.patientsTable.set(cached.data);
                this.totalPatients.set(cached.total);
                return;
            }
        }

        try {
            this.loadingService.set(true);
            let finalSearchTerm = SecurityUtil.sanitizeSearchTerm(searchTerm || '');
            // Se o termo parece um CPF (apenas números, pontos e traços), normalizamos para facilitar a busca no banco
            if (finalSearchTerm && /^[\d\.\-\s]+$/.test(finalSearchTerm.trim()) && finalSearchTerm.trim().replace(/\D/g, '').length >= 3) {
                finalSearchTerm = finalSearchTerm.trim().replace(/\D/g, '');
            }

            const { data, error } = await supabase.rpc('fetch_patients_paginated', {
                p_municipality_id: municipalityId,
                p_limit: limit,
                p_offset: offset,
                p_search_term: finalSearchTerm || null
            });

            if (error) throw error;

            if (data && data.length > 0) {
                const patients = data.map((p: any) => this.mapDbToPatient(p));
                this.patientsTable.set(patients);
                this.totalPatients.set(Number(data[0]?.total_count || 0));
                
                if (!searchTerm) {
                    this.cache.set(cacheKey, { data: patients, total: Number(data[0]?.total_count || 0) }, 2);
                }
            } else {
                this.patientsTable.set([]);
                this.totalPatients.set(0);
            }
        } catch (err: any) {
            console.error('[PatientService] Erro na busca paginada:', err);
            this.toast.show('Erro ao carregar pacientes.', 'error');
        } finally {
            this.loadingService.set(false);
        }
    }

    async addPatient(patient: Patient): Promise<Patient> {
        try {
            this.loadingService.set(true);
            const dbPatient = this.mapPatientToDb(patient);
            delete dbPatient.id;

            const { data, error } = await supabase
                .from('patients')
                .insert(dbPatient)
                .select()
                .single();

            if (error) throw error;

            const newPatient = this.mapDbToPatient(data);
            this.patientsTable.update(list => [...list, newPatient]);
            return newPatient;
        } catch (err: any) {
            this.toast.show(err.message || 'Erro ao salvar paciente.', 'error');
            throw err;
        } finally {
            this.loadingService.set(false);
        }
    }

    async createPatientWithAppointment(
        patientData: Omit<Patient, 'id' | 'registrationDate'> & { registrationDate?: any },
        appointmentData: any,
        operatorId: number
    ) {
        const now = new Date();
        const getSafeDate = (d: any) => {
            if (!d) return now;
            const date = new Date(d);
            return isNaN(date.getTime()) ? now : date;
        };

        const forcedRegDate = getSafeDate(appointmentData.registrationDate || patientData.registrationDate);
        const db = this.ensureDbService();
        if (!db) throw new Error('DatabaseService not linked');

        try {
            this.loadingService.set(true);

            let patientId: number;
            let existingPatient = this.patientsTable().find(p => p.cpf === patientData.cpf);
            const cpfClean = patientData.cpf.replace(/\D/g, '');

            // If not in local cache, check the database before attempting INSERT
            if (!existingPatient) {
                existingPatient = await this.findPatientByCpf(patientData.cpf) || undefined;
                if (existingPatient) {
                    console.log(`[PatientService] Paciente existente encontrado no DB pelo CPF ${patientData.cpf} (clean: ${cpfClean}), ID: ${existingPatient.id}`);
                }
            }

            if (!existingPatient) {
                console.log(`[PatientService] Nenhum paciente encontrado, tentando INSERT com CPF: ${patientData.cpf} (clean: ${cpfClean})`);
                const { data: newP, error: pError } = await supabase
                    .from('patients')
                    .insert(this.mapPatientToDb({
                        ...patientData,
                        id: 0,
                        registrationDate: forcedRegDate,
                        municipalityId: db.currentMunicipalityId()!
                    } as Patient))
                    .select().single();

                if (pError) throw pError;
                patientId = newP.id;
                this.patientsTable.update(list => [...list, this.mapDbToPatient(newP)]);
            } else {
                patientId = existingPatient.id;
                const updatedPatient = {
                    ...existingPatient,
                    ...patientData,
                    id: patientId,
                    municipalityId: existingPatient.municipalityId,
                    registrationDate: existingPatient.registrationDate
                } as Patient;

                const { error: updatePatError } = await supabase
                    .from('patients')
                    .update(this.mapPatientToDb(updatedPatient))
                    .eq('id', patientId);

                if (updatePatError) throw updatePatError;
                this.patientsTable.update(list => list.map(p => p.id === patientId ? updatedPatient : p));
            }

            let resolvedOriginLocation = appointmentData.originLocation || null;
            if (!resolvedOriginLocation) {
                const effectiveAcsId = appointmentData.acsId || patientData.acsId;
                if (effectiveAcsId) {
                    const acs = db.acsTable?.().find((a: any) => a.id === effectiveAcsId);
                    if (acs?.locationId) {
                        const loc = db.locationsTable?.().find((l: any) => l.id === acs.locationId || String(l.id) === String(acs.locationId));
                        if (loc) resolvedOriginLocation = loc.name;
                    }
                }
            }

            const appointmentToInsert = {
                ...appointmentData,
                patientId,
                registrationDate: forcedRegDate,
                municipalityId: db.currentMunicipalityId()!,
                originLocation: resolvedOriginLocation,
                status: 'Aguardando'
            };

            const { data: newApp, error: appError } = await supabase
                .from('appointments')
                .insert(db.mapAppointmentToDb(appointmentToInsert))
                .select().single();

            if (appError) throw appError;
            const appointment = db.mapDbToAppointment(newApp);
            let protocol: string | undefined;

            // Appointment is persisted: post-processing failures must not report the registration as failed
            try {
            protocol = await db.gerarProtocolo(
                'ATENDIMENTO_INICIAL',
                'AGENDAMENTO',
                appointment.id,
                operatorId,
                `Atendimento inicial para: ${patientData.name}`
            );

            await supabase.from('appointments').update({ latest_protocol: protocol }).eq('id', appointment.id);
            appointment.latestProtocol = protocol;
            db.appointmentsTable.update((list: any[]) => [...list, appointment]);

            const proc = db.procedures().find((p: any) => p.id === appointment.procedureId);
            const procedureName = proc?.name || appointmentData.procedureName || 'Procedimento';
            
            // Buscar informações do executor (ente executor)
            let executorName = '-';
            if (appointment.executorId) {
                const executors = db.executorsTable?.() || [];
                const executor = executors.find((e: any) => String(e.id) === String(appointment.executorId));
                executorName = executor?.name || 'ID: ' + appointment.executorId;
            }
            
            // Buscar informações do município (localização)
            const municipality = db.municipalitiesTable().find((m: any) => m.id === appointment.municipalityId);
            const municipalityName = municipality?.name || municipality?.nome || 'Não identificado';
            const scheduleLocation = appointmentData.scheduleLocation || municipality?.nome || '-';
            
            // Buscar informações do ACS pelo ID
            let acsName = '-';
            let acsContact = '-';
            const acsId = patientData.acsId || appointmentData.acsId;
            if (acsId) {
                const acs = db.acsTable?.().find((a: any) => a.id === acsId);
                if (acs) {
                    acsName = acs.name || '-';
                    acsContact = acs.contact || acs.phone || '-';
                }
            }

            // Dados de urgência
            const isUrgency = appointment.urgencyPriority === true;
            const urgencyJustification = appointment.urgencyJustification as any;

            // Criar JSON otimizado para auditoria (legível para operador brasileiro)
            const auditData = {
                nomePaciente: patientData.name,
                patientName: patientData.name,
                patientId: appointment.patientId,
                cpf: this.formatCPF(patientData.cpf),
                telefone: this.formatPhone(patientData.phone),
                dataNascimento: this.formatDate(patientData.birthDate),
                cartaoSus: patientData.susCard || null,
                procedimento: procedureName,
                procedureName: procedureName,
                procedureId: appointment.procedureId,
                categoria: this.normalizeCategory(proc?.category),
                status: appointment.status,
                prioridade: appointmentData.priority || 'Normal',
                priority: appointmentData.priority || 'Normal',
                dataSolicitacao: appointmentData.requestDate,
                dataAgendamento: appointment.schedulingData?.date || null,
                dataCadastro: this.formatDate(patientData.registrationDate),
                horario: appointment.schedulingData?.time || '-',
                local: scheduleLocation,
                location: scheduleLocation,
                municipio: municipalityName,
                enteExecutor: executorName,
                acs: acsName,
                acsName: acsName,
                contatoAcs: acsContact,
                acsContact: acsContact,
                urgencia: isUrgency,
                justificativaUrgencia: urgencyJustification?.text || null,
                imagemUrgencia: urgencyJustification?.imageUrl || null,
                imagemSolicitacao: appointment.requestImage || null,
                endereco: patientData.address ? `${patientData.address.street}, ${patientData.address.district}, ${patientData.address.city}-${patientData.address.uf}` : null,
                observacoes: ''
            };
            
            await db.registrarAuditoria(
                protocol,
                'CRIACAO',
                `Agendamento ${procedureName} para ${patientData.name}`,
                operatorId,
                null,
                auditData
            );

            if (proc?.requiresPreSurgery && !proc?.isGroup) {
                await supabase.from('appointments').update({ is_surgery_parent: true, current_step_index: 0 }).eq('id', appointment.id);
                appointment.isSurgeryParent = true;
                appointment.currentStepIndex = 0;
                db.appointmentsTable.update((list: any[]) => list.map((a: any) => a.id === appointment.id ? appointment : a));
                await db.createNextSurgeryStep(appointment.id, 0, operatorId);
            }

            if (appointment.municipalityId) {
                await db.syncBusinessState(appointment.municipalityId);
            }
            } catch (postErr: any) {
                console.error('[PatientService] Post-insert processing failed:', postErr);
                if (!db.appointmentsTable().some((a: any) => a.id === appointment.id)) {
                    db.appointmentsTable.update((list: any[]) => [...list, appointment]);
                }
                this.toast.show('Paciente adicionado à fila, mas houve falha ao concluir o registro de auditoria.', 'warning');
            }

            return { appointment, protocol };
        } catch (err: any) {
            this.toast.show(err.message || 'Erro no processo de cadastro.', 'error');
            throw err;
        } finally {
            this.loadingService.set(false);
        }
    }

    async updatePatient(patient: Patient, operatorId: number, protocoloReferencia?: string): Promise<string> {
        try {
            const oldPat = this.patientsTable().find(p => p.id === patient.id);

            // Verifica se houve alteração real em campos demográficos
            const changes: string[] = [];
            if (oldPat) {
                if ((oldPat.name || '').trim() !== (patient.name || '').trim()) {
                    changes.push(`Nome: ${oldPat.name || '(vazio)'} -> ${patient.name}`);
                }
                if ((oldPat.socialName || '').trim() !== (patient.socialName || '').trim()) {
                    changes.push(`Nome Social: ${oldPat.socialName || '(vazio)'} -> ${patient.socialName || '(vazio)'}`);
                }
                if ((oldPat.susCard || '').trim() !== (patient.susCard || '').trim()) {
                    changes.push(`Cartão SUS: ${oldPat.susCard || '(vazio)'} -> ${patient.susCard || '(vazio)'}`);
                }
                const oldBirth = oldPat.birthDate ? new Date(oldPat.birthDate).toISOString().split('T')[0] : '';
                const newBirth = patient.birthDate ? new Date(patient.birthDate).toISOString().split('T')[0] : '';
                if (oldBirth !== newBirth) {
                    changes.push(`Data Nascimento: ${this.formatDate(oldPat.birthDate)} -> ${this.formatDate(patient.birthDate)}`);
                }
                const oldPhone = (oldPat.phone || '').replace(/\D/g, '');
                const newPhone = (patient.phone || '').replace(/\D/g, '');
                if (oldPhone !== newPhone) {
                    changes.push(`Telefone: ${this.formatPhone(oldPat.phone)} -> ${this.formatPhone(patient.phone)}`);
                }
                const oldSecPhone = (oldPat.secondaryPhone || '').replace(/\D/g, '');
                const newSecPhone = (patient.secondaryPhone || '').replace(/\D/g, '');
                if (oldSecPhone !== newSecPhone) {
                    changes.push(`Telefone Secundário: ${this.formatPhone(oldPat.secondaryPhone)} -> ${this.formatPhone(patient.secondaryPhone)}`);
                }
                if (oldPat.acsId !== patient.acsId) {
                    changes.push(`ACS: ${oldPat.acsId || '(vazio)'} -> ${patient.acsId || '(vazio)'}`);
                }
                const oldAddr = oldPat.address || {} as any;
                const newAddr = patient.address || {} as any;
                if (
                    (oldAddr.zip || '') !== (newAddr.zip || '') ||
                    (oldAddr.city || '') !== (newAddr.city || '') ||
                    (oldAddr.street || '') !== (newAddr.street || '') ||
                    (oldAddr.number || '') !== (newAddr.number || '') ||
                    (oldAddr.district || '') !== (newAddr.district || '') ||
                    (oldAddr.complemento || '') !== (newAddr.complemento || '')
                ) {
                    changes.push(`Endereço alterado`);
                }
                if (oldPat.residenceProofImage !== patient.residenceProofImage) {
                    changes.push(`Comprovante de residência atualizado`);
                }
            }

            // Se nenhum dado demográfico mudou em relação ao registro existente, não faz update no banco nem gera log
            if (oldPat && changes.length === 0) {
                return 'EDICAO_FINALIZADA';
            }

            const dbPatient = this.mapPatientToDb(patient);
            delete dbPatient.cpf; // CPF é imutável - excluído do update para evitar conflito de constraint unique
            const { error } = await supabase.from('patients').update(dbPatient).eq('id', patient.id);
            if (error) throw error;

            if (protocoloReferencia && this.dbService) {
                // Recuperar status atual do atendimento para não retroceder visualmente para 'ATIVO' em relatórios
                const app = this.dbService?.appointmentsTable?.().find((a: any) => 
                    a.latestProtocol === protocoloReferencia || a.subProtocol === protocoloReferencia
                );

                const dadosEdicao = {
                    nomePaciente: patient.name,
                    cpf: this.formatCPF(patient.cpf),
                    telefone: this.formatPhone(patient.phone),
                    dataNascimento: this.formatDate(patient.birthDate),
                    cartaoSus: patient.susCard || null,
                    status: app?.status || 'ATIVO',
                    dataAtualizacao: new Date().toLocaleString('pt-BR')
                };
                const changesSummary = changes.length > 0 ? ` (${changes.join('; ')})` : '';
                await this.dbService.registrarAuditoria(protocoloReferencia, 'EDICAO_DADOS', `Alteração de dados demográficos: ${patient.name}${changesSummary}`, operatorId, null, dadosEdicao);
            }

            this.patientsTable.update(list => list.map(p => p.id === patient.id ? patient : p));
            return 'EDICAO_FINALIZADA';
        } catch (err: any) {
            this.toast.show(err.message || 'Erro ao atualizar paciente.', 'error');
            throw err;
        }
    }

    async deletePatient(id: number, operatorId?: number) {
        const oldPat = this.patientsTable().find(p => p.id === id);
        if (!oldPat) throw new Error('Paciente não encontrado.');

        try {
            this.loadingService.set(true);
            let protocol = null;
            const db = this.ensureDbService();

            if (db && operatorId) {
                protocol = await db.gerarProtocolo('EXCLUSAO_LOGICA', 'PACIENTE', id, operatorId, `Ocultação de paciente ID: ${id} - ${oldPat.name}`);
                
                const dadosCancelamento = {
                    nomePaciente: oldPat.name,
                    cpf: this.formatCPF(oldPat.cpf),
                    telefone: this.formatPhone(oldPat.phone),
                    dataNascimento: this.formatDate(oldPat.birthDate),
                    cartaoSus: oldPat.susCard || null,
                    dataArquivamento: new Date().toLocaleString('pt-BR'),
                    motivo: 'Arquivamento pelo operador (histórico preservado)'
                };
                
                await db.registrarAuditoria(protocol, 'CANCELAMENTO', `Arquivamento de paciente (histórico preservado). Nome: ${oldPat.name}`, operatorId, null, dadosCancelamento);
            }

            // Apenas inativa logicamente o paciente, preservando TODOS os agendamentos históricos e cotas
            const { error: patError } = await supabase.from('patients').update({ is_active: false }).eq('id', id);
            if (patError) throw patError;

            this.patientsTable.update(list => list.filter(p => p.id !== id));
            this.toast.show('Paciente arquivado com sucesso.', 'success');
            return protocol;
        } catch (err: any) {
            this.toast.show(err.message || 'Erro ao remover paciente.', 'error');
            throw err;
        } finally {
            this.loadingService.set(false);
        }
    }

    async getPatientById(id: number): Promise<Patient | null> {
        try {
            const { data, error } = await supabase.from('patients').select('*').eq('id', id).maybeSingle();
            if (error || !data) return null;
            return this.mapDbToPatient(data);
        } catch { return null; }
    }

    async findPatientByCpf(cpf: string, birthDate?: string): Promise<Patient | null> {
        // Use search_patients_global RPC which handles CPF format normalization internally
        // (compares both exact match and clean/digits-only match)
        const { data, error } = await supabase.rpc('search_patients_global', {
            p_cpf: cpf,
            p_birth_date: birthDate || null
        });

        if (!error && data && data.length > 0) {
            return this.mapDbToPatient(data[0]);
        }

        // Fallback: if RPC failed or returned empty, try direct query with format variants
        const cleanCpf = cpf.replace(/\D/g, '');
        const cpfVariants = new Set<string>();
        cpfVariants.add(cpf);
        if (cleanCpf.length === 11) {
            cpfVariants.add(cleanCpf);
            cpfVariants.add(cleanCpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4'));
        }

        for (const variant of cpfVariants) {
            let query = supabase.from('patients').select('*').eq('cpf', variant).limit(1);
            if (birthDate) query = query.eq('birth_date', birthDate);
            const retry = await query;
            if (!retry.error && retry.data && retry.data.length > 0) {
                return this.mapDbToPatient(retry.data[0]);
            }
        }

        return null;
    }


    async addAppointmentToExistingPatient(patientId: number, appointmentData: any, operatorId: number) {
        const db = this.ensureDbService();
        if (!db) throw new Error('DatabaseService not linked');

        const now = new Date();

        let resolvedOriginLocation = appointmentData.originLocation || null;
        if (!resolvedOriginLocation && appointmentData.acsId) {
            const acs = db.acsTable?.().find((a: any) => a.id === appointmentData.acsId);
            if (acs?.locationId) {
                const loc = db.locationsTable?.().find((l: any) => l.id === acs.locationId || String(l.id) === String(acs.locationId));
                if (loc) resolvedOriginLocation = loc.name;
            }
        }

        const { data: newApp, error: appError } = await supabase
            .from('appointments')
            .insert({
                patient_id: patientId,
                procedure_id: appointmentData.procedureId,
                municipality_id: appointmentData.municipalityId,
                origin_location: resolvedOriginLocation,
                anatomical_region: appointmentData.anatomicalRegion,
                priority: appointmentData.priority,
                status: 'Aguardando',
                registration_date: now.toISOString(),
                request_date: appointmentData.requestDate,
                request_image_url: appointmentData.requestImage,
                acs_id: (appointmentData as any).acsId,
                selected_sub_exams: appointmentData.selectedSubExams || null
            })
            .select().single();

        if (appError) throw appError;

        const protocol = await db.gerarProtocolo('ATENDIMENTO_INICIAL', 'AGENDAMENTO', newApp.id, operatorId, `Atendimento paciente ID: ${patientId}`);
        await supabase.from('appointments').update({ latest_protocol: protocol }).eq('id', newApp.id);

        const appointment = db.mapDbToAppointment({ ...newApp, latest_protocol: protocol });
        db.appointmentsTable.update((list: any[]) => [...list, appointment]);

        if (appointment.municipalityId) {
            await db.syncBusinessState(appointment.municipalityId);
        }

        return { appointment, protocol };
    }

    async getPatientHistory(patientId: number): Promise<any[]> {
        const db = this.ensureDbService();
        if (!db) return [];
        const patient = this.patientsTable().find(p => p.id === patientId);
        if (!patient) return [];

        const apps = db.appointmentsTable().filter((a: any) => a.patientId === patientId);
        const trail = (patient.auditTrail || []).map((entry: any) => ({
            protocol: entry.protocol,
            timestamp: new Date(entry.date.split('/').reverse().join('-') + 'T' + entry.time),
            action: entry.action,
            target: `Demos: ${patient.name}`,
            user: entry.agent,
            details: entry.details
        }));

        const appTrails = apps.flatMap((a: any) => (a.auditTrail || []).map((entry: any) => ({
            protocol: entry.protocol,
            timestamp: new Date(entry.date.split('/').reverse().join('-') + 'T' + entry.time),
            action: entry.action,
            target: `Agend.: ${a.id}`,
            user: entry.agent,
            details: entry.details
        })));

        return [...trail, ...appTrails].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }

    mapDbToPatient(p: any): Patient {
        const patient: Patient = {
            id: p.id,
            cpf: p.cpf,
            name: p.name,
            socialName: p.social_name || p.socialName,
            birthDate: p.birth_date || p.birthDate,
            susCard: p.sus_card || p.susCard,
            phone: p.phone,
            secondaryPhone: p.secondary_phone,
            address: {
                zip: (p.address?.zip || p.address_zip || p.zip || p.patient_zip || p.addressZip || ''),
                street: (p.address?.street || p.address_street || p.street || p.patient_street || p.addressStreet || ''),
                number: (p.address?.number || p.address_number || p.number || p.patient_number || p.addressNumber || ''),
                district: (p.address?.district || p.address_district || p.district || p.patient_district || p.addressDistrict || ''),
                city: (p.address?.city || p.address_city || p.city || p.patient_city || p.addressCity || ''),
                uf: (p.address?.uf || p.address_uf || p.uf || p.patient_uf || p.addressUf || ''),
                complemento: (p.address?.complemento || p.address_complement || p.complement || p.patient_complement || p.addressComplement || '')
            },
            residenceProofDate: p.residence_proof_date || p.residenceProofDate || null,
            residenceProofImage: p.residence_proof_image_url || p.residenceProofImage || '',
            acsId: p.acs_id || p.acsId || null,
            municipalityId: p.municipality_id || p.municipalityId,
            registrationDate: new Date(p.registration_date || p.created_at),
            auditTrail: p.audit_trail
        };

        // Safety check for municipality name injection (Issue 3)
        if (patient.name && patient.name.length > 3 && patient.name === patient.address.city) {
            console.warn(`[PatientService] Detected potential municipality name injection: ${patient.name}`);
        }

        return patient;
    }

    private formatCPF(cpf: string | undefined): string {
        if (!cpf) return '-';
        const cleaned = cpf.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return `${cleaned.slice(0,3)}.${cleaned.slice(3,6)}.${cleaned.slice(6,9)}-${cleaned.slice(9)}`;
        }
        return cpf;
    }

    private formatPhone(phone: string | undefined): string {
        if (!phone) return '-';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0,2)}) ${cleaned.slice(2,6)}-${cleaned.slice(6)}`;
        }
        if (cleaned.length === 11) {
            return `(${cleaned.slice(0,2)}) ${cleaned.slice(2,7)}-${cleaned.slice(7)}`;
        }
        return phone;
    }

    private formatDate(dateStr: string | null | undefined): string {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleDateString('pt-BR');
        } catch {
            return dateStr;
        }
    }

    private normalizeCategory(category: string | undefined): string {
        if (!category) return '-';
        return category
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    mapPatientToDb(p: Patient): any {
        return {
            cpf: p.cpf,
            name: p.name,
            social_name: p.socialName,
            sus_card: p.susCard,
            birth_date: PatientService.normalizeDate(p.birthDate),
            phone: p.phone,
            secondary_phone: p.secondaryPhone || null,
            address_zip: p.address?.zip || null,
            address_street: p.address?.street || null,
            address_number: p.address?.number || null,
            address_district: p.address?.district || null,
            address_city: p.address?.city || null,
            address_uf: p.address?.uf || null,
            address_complement: p.address?.complemento || null,
            municipality_id: p.municipalityId,
            residence_proof_image_url: p.residenceProofImage,
            residence_proof_date: PatientService.normalizeDate(p.residenceProofDate),
            audit_trail: p.auditTrail,
            acs_id: p.acsId || null
        };
    }
}
